import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { Course } from '@/types';
import { getInitials } from '@/lib';
import { BookOpen, CalendarDays, Users, Star } from 'lucide-react';

interface CourseCardProps {
    course: Course;
    onClick?: () => void;
}

const CourseCard = ({ course, onClick }: CourseCardProps) => {
    const bannerUrl =
        course.course_banner || '/images/static-images/course-banner.jpg';
    const lecturerFirstName = course.lecturer?.first_name || '';
    const lecturerLastName = course.lecturer?.last_name || '';
    const lecturerLabel =
        course.lecturer_full_name ||
        `${lecturerFirstName} ${lecturerLastName}`.trim() ||
        'Unassigned instructor';
    const instructorInitials = getInitials(
        lecturerFirstName,
        lecturerLastName
    );
    const averageRating = course.average_rating ?? 0;
    const updatedLabel = (() => {
        if (!course.updated_date) return '-';
        const date = new Date(course.updated_date);
        if (Number.isNaN(date.getTime())) return course.updated_date;
        return date.toLocaleDateString();
    })();

    return (
        <Card
            className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-none bg-card/50 backdrop-blur-sm hover:-translate-y-1 cursor-pointer flex flex-col"
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && onClick) {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            {/* Course Banner */}
            <div className="relative -mx-6 -mt-6 h-48 overflow-hidden rounded-t-xl">
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10" />
                <img
                    src={bannerUrl}
                    alt={course.course_name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
            </div>

            <div className="flex flex-col flex-1">
                <CardHeader className="pb-2 h-20">
                    {/* Course Name */}
                    <h3 className="text-xl font-bold line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                        {course.course_name}
                    </h3>

                    {/* Board Display */}
                    {course.board_display && (
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                            {course.board_display?.board_name}
                        </p>
                    )}
                </CardHeader>

                <CardContent className="space-y-4 flex-1">
                    {/* Instructor Info */}
                    <div className="flex items-center gap-3 bg-muted/30 p-2 rounded-lg">
                        <Avatar className="h-10 w-10 border-2 border-background">
                            <AvatarImage
                                src={course.lecturer?.photo_url || undefined}
                                alt={lecturerLabel}
                            />
                            <AvatarFallback className="bg-primary/10 text-primary">{instructorInitials}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                            <p className="text-sm font-semibold">{lecturerLabel}</p>
                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-tight">Instructor</p>
                        </div>
                    </div>
                </CardContent>
            </div>

            <CardFooter className="flex flex-wrap items-center gap-3 border-t pt-4 min-h-14">
                {/* Number of Enrollments */}
                <div className="flex items-center gap-1 text-sm text-muted-foreground" title="Students Enrolled">
                    <Users className="h-4 w-4" />
                    <span>{course.students_enrolled}</span>
                </div>

                {/* Total Chapters */}
                <div className="flex items-center gap-1 text-sm text-muted-foreground" title="Chapters">
                    <BookOpen className="h-4 w-4" />
                    <span>{course.course_section}</span>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1 text-sm text-muted-foreground" title="Average Rating">
                    <Star
                        className={`h-4 w-4 ${averageRating > 0 ? 'fill-yellow-400 text-yellow-400' : ''
                            }`}
                    />
                    <span>{averageRating.toFixed(1)}</span>
                </div>

                <div
                    className="ml-auto flex items-center gap-1 text-sm text-muted-foreground"
                    title="Updated At"
                >
                    <CalendarDays className="h-4 w-4" />
                    <span>{updatedLabel}</span>
                </div>
            </CardFooter>
        </Card>
    );
};

export default CourseCard;
